import { z } from "zod";
import type {
  CreateAssignmentPayload,
  FormType,
  VisitCategory,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";

export const scopeTargetSchema = z.object({
  tenantId: z.string().min(1),
  clientId: z.string().min(1).optional(),
  brandId: z.string().min(1).optional(),
});

export type ScopeTarget = z.infer<typeof scopeTargetSchema>;

const ASSIGNABLE_FORM_TYPES = [
  "check_in",
  "assessment",
  "further_assessment",
  "different_trade_assessment",
  "another_opinion",
  "repair",
  "different_trade_repair",
  "assumed_recall",
  "recall",
  "check_out",
  "pm_visit",
  "survey_visit",
  "walkthrough_visit",
  "recurring_visit",
  "dispatch_call",
] as const satisfies readonly FormType[];

const ASSIGNABLE_WORK_ORDER_TYPES = [
  "reactive",
  "pm",
  "project",
  "survey",
  "recurring",
  "dispatch_call",
] as const satisfies readonly VisitCategory[];

export const scopeAssignmentsSchema = z.object({
  targets: z.array(scopeTargetSchema).min(1),
  formType: z.enum(ASSIGNABLE_FORM_TYPES).optional(),
  workOrderType: z.enum(ASSIGNABLE_WORK_ORDER_TYPES).optional(),
});

export type ScopeAssignments = z.infer<typeof scopeAssignmentsSchema>;

const FORM_TYPE_LEGACY_LABEL_SLUGS: Record<string, FormType> = {
  pm: "pm_visit",
  surveys: "survey_visit",
  recurring: "recurring_visit",
};

const slugify = (label: string | undefined) =>
  label
    ?.trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export const toFormTypeSlug = (label: string | undefined): ScopeAssignments["formType"] => {
  const slug = slugify(label);
  return (
    ASSIGNABLE_FORM_TYPES.find((value) => value === slug) ??
    FORM_TYPE_LEGACY_LABEL_SLUGS[slug ?? ""]
  );
};

export const toWorkOrderTypeSlug = (label: string | undefined): VisitCategory | undefined => {
  const slug = slugify(label);
  return ASSIGNABLE_WORK_ORDER_TYPES.find((value) => value === slug);
};

interface BuildAssignmentPayloadsInput {
  scope: ScopeAssignments;
  templateId: string;
  tradeIds: number[];
  subTradeIds: number[];
}

const getTradeSlots = (tradeIds: number[], subTradeIds: number[]) => {
  if (tradeIds.length === 0) return [{}];
  if (tradeIds.length === 1 && subTradeIds.length > 0) {
    return subTradeIds.map((subtradeId) => ({ tradeId: tradeIds[0], subtradeId }));
  }
  return tradeIds.map((tradeId) => ({ tradeId }));
};

export function buildAssignmentPayloads({
  scope,
  templateId,
  tradeIds,
  subTradeIds,
}: BuildAssignmentPayloadsInput): CreateAssignmentPayload[] | null {
  const { formType, workOrderType, targets } = scope;
  if (!formType || !workOrderType) return null;
  const tradeSlots = getTradeSlots(tradeIds, subTradeIds);
  return targets.flatMap((target) =>
    tradeSlots.map((tradeSlot) => ({
      ...target,
      workOrderType,
      formType,
      templateId,
      ...tradeSlot,
    })),
  );
}
